/**
 * TeamChatTestPage — test form for the providerless team-chat channel
 * (roadmap #73/#77). Unlike the other channels there are no recipients,
 * templates or scheduling: the destination IS the incoming-webhook URL of
 * the customer's Slack / Teams / Discord / Google Chat channel, and the
 * message is always custom (title + text + severity).
 */

import React, { useState } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
} from "react-hook-form";
import { Box, Grid, IconButton, MenuItem, TextField } from "@mui/material";
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { useNotify, useTranslate } from "react-admin";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { sendTeamChatTestNotification } from "../services/testService";
import AccountAppPicker from "./sections/AccountAppPicker";
import RgSection from "./sections/RgSection";
import TestPageShell from "./TestPageShell";

const TARGETS = [
  { id: "slack", label: "Slack" },
  { id: "teams", label: "Microsoft Teams" },
  { id: "discord", label: "Discord" },
  { id: "googlechat", label: "Google Chat" },
  { id: "wecom", label: "WeCom (WeChat Work)" },
  { id: "mattermost", label: "Mattermost" },
  { id: "rocketchat", label: "Rocket.Chat" },
];

const SEVERITIES = [
  { id: "", labelKey: "resources.test.teamChat.severityNone" },
  { id: "info", label: "Info" },
  { id: "success", label: "Success" },
  { id: "warning", label: "Warning" },
  { id: "critical", label: "Critical" },
];

interface TeamChatFormValues {
  accountId: string;
  appId: string;
  target: string;
  webhookUrls: { value: string }[];
  title: string;
  text: string;
  severity: string;
  messagePriority: "STANDARD" | "CRITICAL";
  webhookUrl: string;
}

const emptyValues = (): TeamChatFormValues => ({
  accountId: "",
  appId: "",
  target: "slack",
  webhookUrls: [{ value: "" }],
  title: "",
  text: "",
  severity: "",
  messagePriority: "STANDARD",
  webhookUrl: "",
});

export const TeamChatTestPage: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const methods = useForm<TeamChatFormValues>({
    defaultValues: emptyValues(),
    mode: "all",
    reValidateMode: "onChange",
    shouldFocusError: true,
  });
  const { fields, append, remove } = useFieldArray({
    control: methods.control,
    name: "webhookUrls",
  });

  const onSubmit = async (values: TeamChatFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      const response = await sendTeamChatTestNotification({
        accountId: values.accountId,
        appId: values.appId,
        target: values.target,
        webhookUrls: values.webhookUrls
          .map((u) => u.value.trim())
          .filter(Boolean),
        title: values.title.trim() || undefined,
        text: values.text,
        severity: values.severity || undefined,
        messageType:
          values.messagePriority === "CRITICAL" ? "critical" : "normal",
        webhookUrl: values.webhookUrl.trim() || undefined,
        origin: "admin-test",
      });
      if (response.success) {
        notify("resources.test.notifications.teamChatSent", {
          type: "success",
        });
        methods.reset(emptyValues());
      } else {
        notify(
          response.message ??
            translate("resources.test.notifications.teamChatSendFailed"),
          { type: "error" },
        );
      }
    } catch (err: unknown) {
      notify(
        extractErrorMessage(
          err,
          "resources.test.notifications.teamChatSendFailed",
          translate,
        ),
        { type: "error" },
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <FormProvider {...methods}>
      <TestPageShell
        titleKey="resources.test.teamChat.title"
        submitting={submitting}
        onSubmit={methods.handleSubmit(onSubmit)}
      >
        <RgSection
          label={translate("resources.test.sections.accountApplication", {
            _: "Account & application",
          })}
        >
          <AccountAppPicker />
        </RgSection>

        <RgSection
          label={translate("resources.test.teamChat.destination", {
            _: "Destination",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={4}>
              <Controller
                name="target"
                control={methods.control}
                rules={{
                  required: translate(
                    "resources.test.teamChat.targetRequired",
                  ),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    required
                    label={translate("resources.test.teamChat.target")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={
                      fieldState.error?.message ??
                      translate("resources.test.teamChat.targetHelp")
                    }
                  >
                    {TARGETS.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              {fields.map((f, idx) => (
                <Box
                  key={f.id}
                  sx={{ display: "flex", gap: 1, mb: idx < fields.length - 1 ? 1.5 : 0 }}
                >
                  <Controller
                    name={`webhookUrls.${idx}.value`}
                    control={methods.control}
                    rules={{
                      required: translate(
                        "resources.test.teamChat.webhookUrlRequired",
                      ),
                      pattern: {
                        value: /^https:\/\/.+/i,
                        message: translate(
                          "resources.test.teamChat.webhookUrlHttps",
                        ),
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        required
                        label={`${translate("resources.test.teamChat.webhookUrlField")} ${fields.length > 1 ? idx + 1 : ""}`.trim()}
                        placeholder="https://hooks.slack.com/services/…"
                        InputLabelProps={{ shrink: true }}
                        error={Boolean(fieldState.error)}
                        helperText={fieldState.error?.message}
                      />
                    )}
                  />
                  {fields.length > 1 && (
                    <IconButton
                      aria-label={translate("ra.action.remove")}
                      onClick={() => remove(idx)}
                      sx={{ alignSelf: "center" }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                  {idx === fields.length - 1 && fields.length < 10 && (
                    <IconButton
                      aria-label={translate("ra.action.add")}
                      onClick={() => append({ value: "" })}
                      sx={{ alignSelf: "center" }}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              ))}
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.message", {
            _: "Message",
          })}
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={8}>
              <Controller
                name="title"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.teamChat.titleField")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Controller
                name="severity"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={translate("resources.test.teamChat.severity")}
                    InputLabelProps={{ shrink: true }}
                  >
                    {SEVERITIES.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label ?? translate(s.labelKey!, { _: "None" })}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="text"
                control={methods.control}
                rules={{
                  required: translate("resources.test.teamChat.textRequired"),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    multiline
                    minRows={3}
                    label={translate("resources.test.teamChat.textField")}
                    InputLabelProps={{ shrink: true }}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>

        <RgSection
          label={translate("resources.test.sections.delivery", {
            _: "Delivery options",
          })}
          last
        >
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            <Grid item xs={12} md={6}>
              <Controller
                name="messagePriority"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    select
                    fullWidth
                    label={translate("resources.test.fields.messagePriority")}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="STANDARD">
                      {translate(
                        "resources.test.choices.messagePriority.STANDARD",
                      )}
                    </MenuItem>
                    <MenuItem value="CRITICAL">
                      {translate(
                        "resources.test.choices.messagePriority.CRITICAL",
                      )}
                    </MenuItem>
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="webhookUrl"
                control={methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={translate("resources.test.fields.webhookUrl")}
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </RgSection>
      </TestPageShell>
    </FormProvider>
  );
};

export default TeamChatTestPage;
